import React, {useState, useContext, useEffect, useMemo} from "react";
import {
    Box, Stack, Typography, IconButton, Chip,
    CircularProgress, Table, TableHead, TableRow,
    TableCell, TableBody, Tooltip, Button, Divider, Input,
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
    Add01Icon, CheckIcon, Copy01Icon, LockOpenIcon, LockPasswordIcon,
    Refresh01Icon, Search01Icon,
} from "@hugeicons/core-free-icons";
import {TextField, InputAdornment, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {Context} from "../../../context/AppContext";
import {AddMCPServerModal} from "./components/AddMCPServerModal";
import {copyToClipboard} from "../../../utils/functions";
import {toast} from "react-toastify";
import useConfirm from "../../../hooks/useConfirm";
import {useNavigate} from "react-router-dom";
import {lightBlue, orange} from "@mui/material/colors";

const cellHeader = {
    fontWeight: 700, fontSize: 11, py: 1,
    color: "text.secondary", textTransform: "uppercase",
    letterSpacing: 0.5,
}

export const MCPDiscoveryPage = ({project}) => {
    const {
        state,
        createMCPServer,
        getServerDetails,
        getAllServersWithDetails
    } = useContext(Context);
    const {mcpServers: servers, mcpLoading: loading} = state;

    const navigate = useNavigate();

    const [openAdd, setOpenAdd] = useState(false);

    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [copiedId, setCopiedId] = useState(null);



    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), 300);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        if (project?.id) {
            if (project?.configuration?.mcp_registry) {
                getAllServersWithDetails(project.id);
            } else {
                toast.info("Please update MCP client in configuration")
                navigate(`/project/${project?.id}`)
            }
        }
    }, [project.id]);

    const copyHandler = async (server) => {
        try {
            await copyToClipboard(server.endpoint);
            toast.success("Endpoint copied.");
            setCopiedId(server.id)
            setTimeout(() => setCopiedId(null), 1500);
        } catch (e) {
            toast.error("Failed to copy.");
        }
    };

    const filteredServers = useMemo(() => {
        const q = debouncedQuery.trim().toLowerCase();
        return servers.filter((s) => {
            const matchesQuery =
                !q ||
                s.name?.toLowerCase().includes(q) ||
                s.endpoint?.toLowerCase().includes(q);
            const matchesStatus =
                statusFilter === "all" ||
                (statusFilter === "active" && s.active) ||
                (statusFilter === "inactive" && !s.active);
            return matchesQuery && matchesStatus;
        });
    }, [servers, debouncedQuery, statusFilter]);


    const addMCPServer = async (payload) => {
        await createMCPServer(payload);
        await getAllServersWithDetails(project.id);
    };


    const {activeCount, totalTools, totalPrompts} = useMemo(() => {
        const activeCount = servers.filter((s) => s.active).length;
        const totalTools = servers.reduce((acc, s) => acc + (s.tools?.length || 0), 0);
        const totalPrompts = servers.reduce((acc, s) => acc + Object.keys(s.prompts || {}).length, 0);
        return {activeCount, totalTools, totalPrompts};
    }, [servers]);

    return (
        <Box sx={{
            height: "100%",
            maxWidth: "xl",
            width: "90%",
            mx: "auto",
            minWidth: 0,
            boxSizing: "border-box",
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2
        }}>


            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight={600}>MCP Servers</Typography>
                <Stack direction="row" gap={1.5} alignItems="center">

                    <Button
                        variant="modal"
                        color="secondary"
                        size="small"
                        startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
                        onClick={() => setOpenAdd(true)}
                    >
                        Create
                    </Button>
                    <Divider orientation="vertical" variant="middle" flexItem/>
                    <Button
                        variant={"ghost"}
                        size="small" color="secondary"
                        onClick={() => getAllServersWithDetails(project.id)}
                        startIcon={<HugeiconsIcon icon={Refresh01Icon} size={18}/>}
                        sx={{color: "grey.700", pr: 2, pl: 1}}
                    >
                        Reload
                    </Button>
                </Stack>
            </Stack>


            {/*<Stack direction="row" gap={2}>*/}
            {/*  {[*/}
            {/*    [servers.length, "Servers"],*/}
            {/*    [totalTools, "Total tools"],*/}
            {/*    [totalPrompts, "Total prompts"],*/}
            {/*  ].map(([val, lbl]) => (*/}
            {/*    <Box key={lbl} sx={{bgcolor: "grey.100", borderRadius: 2, px: 3, py: 1.5, textAlign: "center"}}>*/}
            {/*      <Typography variant="h4" fontWeight={600}>{val}</Typography>*/}
            {/*      <Typography variant="caption" color="text.secondary">{lbl}</Typography>*/}
            {/*    </Box>*/}
            {/*  ))}*/}
            {/*</Stack>*/}


            <Stack
                direction="row" gap={1}
                useFlexGap flexWrap={"wrap"} alignItems="center"
                sx={{pt: 2}}
            >
                <Box sx={{maxWidth: "280px", width: "100%"}}>
                    <Input
                        size="small"
                        placeholder="Search by name or endpoint..."
                        value={query}
                        disableUnderline={true}
                        onChange={(e) => setQuery(e.target.value)}
                        sx={{
                            width: "100%", px: 1.5, py: 0.5,
                            borderRadius: 2,
                            bgcolor: "grey.100"
                        }}
                        startAdornment={<HugeiconsIcon icon={Search01Icon} size={16} style={{marginRight: "6px"}}/>}

                    />
                </Box>
                <ToggleButtonGroup
                    value={statusFilter} exclusive
                    onChange={(e, val) => val && setStatusFilter(val)}
                    size="small"
                    sx={{
                        // ml: "auto",
                        // borderRadius: 3,
                        border: "none",
                        gap: 0.5,
                        "& .MuiToggleButton-root": {
                            border: "none",
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: 12, px: 2, py: 0.5,
                            color: "text.secondary",
                            "&:hover": {
                                bgcolor: "grey.300",
                            },
                            "&.Mui-selected": {
                                bgcolor: "grey.200",
                                color: "grey.700", fontWeight: "bold",
                                // "&:hover": {bgcolor: "secondary.dark"},
                            },
                        },
                    }}
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="active">Active</ToggleButton>
                    <ToggleButton value="inactive">Inactive</ToggleButton>
                </ToggleButtonGroup>

                <Typography variant="caption" color="text.secondary" sx={{ml: "auto", whiteSpace: "nowrap"}}>
                    Servers: <span style={{fontWeight: 600}}>{activeCount} / {servers.length}</span>
                </Typography>
                <Divider orientation="vertical" variant="middle" flexItem/>

                <Typography variant="caption" color="text.secondary" sx={{whiteSpace: "nowrap"}}>
                    Tools: <span style={{fontWeight: 600}}>{totalTools}</span>
                </Typography>
                <Divider orientation="vertical" variant="middle" flexItem/>
                <Typography variant="caption" color="text.secondary" sx={{whiteSpace: "nowrap"}}>
                    Prompts: <span style={{fontWeight: 600}}>{totalPrompts}</span>
                </Typography>
                {/*<Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap"}}>*/}
                {/*  {filteredServers.length} of {servers.length}*/}
                {/*</Typography>*/}
            </Stack>


            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 4}}>
                    <CircularProgress size={24}/>
                </Box>
            ) : (
                <Box className={"border-shadow"} sx={{borderRadius: 2, overflow: "hidden"}}>
                    <Table size="small" sx={{tableLayout: "auto"}}>
                        <TableHead>
                            <TableRow sx={{bgcolor: "grey.50"}}>
                                <TableCell sx={cellHeader}>Name</TableCell>
                                <TableCell sx={cellHeader}>Endpoint</TableCell>
                                <TableCell sx={{...cellHeader, px: 0.5}}>Secure</TableCell>
                                <TableCell sx={cellHeader}>Tools/Prompts</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredServers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center"
                                               sx={{py: 4, color: "text.secondary", fontSize: 13}}>
                                        No servers match your search
                                    </TableCell>
                                </TableRow>
                            ) : filteredServers.map((server) => {
                                const toolCount = server?.tools?.length || 0;
                                const promptCount = Object.keys(server?.prompts || {}).length;
                                const isSecure = server?.auth_token === true;

                                return (
                                    <TableRow key={server.id} hover sx={{cursor: "pointer"}}
                                              onClick={() => navigate(`/project/${project?.id}/mcp-registry/${server?.id}`)}>
                                        <TableCell sx={{fontSize: 13, fontWeight: 600}}>
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                <Box sx={{
                                                    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                                                    bgcolor: server.active ? "success.main" : "error.main",
                                                }}/>
                                                {server.name}
                                            </Stack>
                                        </TableCell>

                                        <TableCell>
                                            <Stack
                                                sx={{"&:hover > .copy-btn": {opacity: "1 !important"}}}
                                                direction="row"
                                                alignItems="center"
                                                gap={0.5}
                                            >
                                                <Typography
                                                    fontSize={11}
                                                    fontFamily="monospace"
                                                    color="text.secondary"
                                                >
                                                    {server.endpoint}
                                                </Typography>
                                                <Tooltip title="Copy endpoint">
                                                    <IconButton
                                                        size="small"
                                                        className="copy-btn"
                                                        sx={{opacity: copiedId === server.id ? 1 : 0, p: 0.25}}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copyHandler(server);
                                                        }}
                                                    >
                                                        {copiedId === server.id ? <HugeiconsIcon icon={CheckIcon} size={13} color="green"/> : <HugeiconsIcon icon={Copy01Icon} size={13}/>} 
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>

                                        </TableCell>
                                        <TableCell padding={"none"} align={"left"}>
                                            <Tooltip
                                                title={isSecure ? "Connection is secure" : "Connection may not be secure"}>
                                                <Box>
                                                    <Chip
                                                        icon={isSecure ? <HugeiconsIcon icon={LockPasswordIcon} size={13}/> :
                                                            <HugeiconsIcon icon={LockOpenIcon} size={13}/>} 
                                                        label={isSecure ? "Connection is secure" : "Connection may not be secure"}
                                                        size="small"
                                                        color={isSecure ? "info" : "warning"}
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: 12, pl: 1, pr: 0.5, py: 1,
                                                            fontWeight: 500, borderRadius: 2,
                                                            bgcolor: isSecure ? lightBlue[50] : orange[100],
                                                            border: "none"
                                                        }}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        </TableCell>

                                        <TableCell align={"left"}>
                                            <Typography color={"grey.600"} fontSize={11} fontWeight={600}>
                                                {toolCount} / {promptCount}
                                            </Typography>
                                        </TableCell>


                                        {/*<TableCell align={"center"}>*/}
                                        {/*  <Chip*/}
                                        {/*    label={server.active ? "Active" : "Inactive"}*/}
                                        {/*    size="small"*/}
                                        {/*    color={server.active ? "success" : "error"}*/}
                                        {/*    variant="outlined"*/}
                                        {/*    sx={{fontSize: 12, px: 1}}*/}
                                        {/*  />*/}
                                        {/*</TableCell>*/}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            )}

            <AddMCPServerModal
                open={openAdd}
                onClose={() => setOpenAdd(false)}
                onSave={addMCPServer}
                defaultFlowUid={project.id}
            />

        </Box>
    );
};

export default MCPDiscoveryPage;
